"use client";

import { useLayoutEffect, useMemo, useRef, Fragment } from "react";
import Image from "next/image";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { EASE } from "@/lib/animations";
import {
  ABOUT_HERITAGE_SHOWCASE_CONTENT,
  HERITAGE_COLORS,
} from "@/data/about-heritage-showcase";
import styles from "./HeritageShowcase.module.css";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger);
}

export function HeritageShowcase() {
  const sectionRef = useRef<HTMLElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const descriptionRef = useRef<HTMLParagraphElement>(null);
  const collageRef = useRef<HTMLDivElement>(null);

  const { heading, description, images } = ABOUT_HERITAGE_SHOWCASE_CONTENT;

  const words = useMemo(
    () => description.split(" ").filter(Boolean),
    [description]
  );

  useLayoutEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const ctx = gsap.context(() => {
      /* ─── HEADING reveal ─── */
      if (headingRef.current) {
        if (reduced) {
          gsap.set(headingRef.current, {
            opacity: 1,
            y: 0,
            filter: "blur(0px)",
          });
        } else {
          gsap.fromTo(
            headingRef.current,
            { opacity: 0, y: 80, filter: "blur(14px)" },
            {
              opacity: 1,
              y: 0,
              filter: "blur(0px)",
              duration: 1.4,
              ease: EASE.out,
              scrollTrigger: {
                trigger: section,
                start: "top 75%",
                once: true,
              },
            }
          );
        }
      }

      /* ─── DESCRIPTION scroll-fill ─── */
      if (descriptionRef.current) {
        const wordEls =
          descriptionRef.current.querySelectorAll<HTMLSpanElement>(
            "[data-word]"
          );

        if (reduced) {
          wordEls.forEach((el) => {
            gsap.set(el, { color: HERITAGE_COLORS.filled });
          });
        } else if (wordEls.length) {
          gsap.set(wordEls, { color: HERITAGE_COLORS.faded });

          const tl = gsap.timeline({
            scrollTrigger: {
              trigger: descriptionRef.current,
              start: "top 82%",
              end: "bottom 55%",
              scrub: 1,
            },
          });

          wordEls.forEach((el, i) => {
            tl.to(
              el,
              {
                color: HERITAGE_COLORS.filled,
                duration: 0.6,
                ease: "power2.out",
              },
              i * 0.06
            );
          });
        }
      }

      /* ─── IMAGE CARDS staggered reveal ─── */
      if (collageRef.current) {
        const cards =
          collageRef.current.querySelectorAll<HTMLDivElement>(
            "[data-heritage-card]"
          );

        if (reduced) {
          gsap.set(cards, { opacity: 1, scale: 1, filter: "blur(0px)" });
        } else if (cards.length) {
          gsap.fromTo(
            cards,
            {
              opacity: 0,
              scale: 0.9,
              filter: "blur(8px)",
            },
            {
              opacity: 1,
              scale: 1,
              filter: "blur(0px)",
              duration: 1.1,
              stagger: {
                each: 0.18,
                from: "random",
              },
              ease: EASE.out,
              scrollTrigger: {
                trigger: collageRef.current,
                start: "top 78%",
                once: true,
              },
            }
          );
        }
      }
    }, section);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full overflow-hidden bg-white"
      aria-labelledby="heritage-heading"
    >
      <div
        className="
          relative mx-auto max-w-[1440px]
          px-5 py-16
          sm:px-8 sm:py-20
          md:px-12 md:py-24
          lg:px-16 lg:py-28
          xl:px-20 xl:py-32
        "
      >
        {/* ═══ HEADER ═══ */}
        <div className="relative z-10 mb-16 max-w-[900px]">
          {/* Giant Heritage heading */}
          <h2
            ref={headingRef}
            id="heritage-heading"
            className="
              m-0 font-extrabold text-neutral-950
              text-[clamp(4rem,13vw,12rem)]
              leading-[0.9] tracking-[-0.045em]
              will-change-[filter,transform,opacity]
            "
          >
            {heading}
          </h2>

          {/* Description with scroll-fill animation */}
          <p
            ref={descriptionRef}
            className="
              mt-6 max-w-[620px]
              text-[clamp(1.1rem,1.6vw,1.5rem)]
              font-medium leading-[1.45] tracking-[-0.015em]
              sm:mt-8
              lg:mt-10 lg:max-w-[720px]
            "
          >
            {words.map((word, i) => (
              <Fragment key={`w-${i}`}>
                <span
                  data-word
                  className="inline-block transition-colors duration-300 will-change-[color]"
                  style={{ color: HERITAGE_COLORS.faded }}
                >
                  {word}
                </span>
                {i < words.length - 1 && " "}
              </Fragment>
            ))}
          </p>
        </div>

        {/* ═══ IMAGE COLLAGE ═══ */}
        <div ref={collageRef} className={styles.collage}>
          {images.map((img) => {
            const cardStyle = {
              "--col-start": img.gridArea.colStart,
              "--col-span": img.gridArea.colSpan,
              "--row-start": img.gridArea.rowStart,
              "--row-span": img.gridArea.rowSpan,
              "--aspect-ratio": img.aspectRatio,
              "--offset-y": `${img.offsetY ?? 0}px`,
            } as React.CSSProperties;

            return (
              <div
                key={img.id}
                data-heritage-card
                className={`${styles.card} group`}
                style={cardStyle}
              >
                {/* Image wrapper - FIXED: removed aspect-[4/3] so custom --aspect-ratio works */}
                <div
                  className="
                    relative w-full overflow-hidden bg-neutral-100
                    rounded-[18px]
                    shadow-[0_12px_40px_-12px_rgba(0,0,0,0.18)]
                    transition-transform duration-700
                    ease-[cubic-bezier(0.16,1,0.3,1)]
                    group-hover:-translate-y-1
                    sm:rounded-[20px]
                    lg:rounded-[22px]
                    2xl:rounded-[26px]
                    after:absolute after:inset-0 after:rounded-[inherit]
                    after:shadow-[inset_0_0_0_1px_rgba(0,0,0,0.06)]
                    after:pointer-events-none after:z-[2]
                  "
                >
                  <Image
                    src={img.src}
                    alt={img.alt}
                    fill
                    className="
                      object-cover
                      transition-transform duration-700
                      ease-[cubic-bezier(0.16,1,0.3,1)]
                      group-hover:scale-[1.05]
                    "
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 45vw, 500px"
                  />
                </div>

                {/* Label */}
                <p
                  className="
                    mt-3.5 text-[14px] font-medium tracking-[-0.005em] text-neutral-950
                    sm:mt-4 sm:text-[15px]
                    2xl:text-[16px]
                  "
                >
                  {img.label}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}